import { Icon, Text, Menu, Button, Spinner } from "@ttc3k/trekker";
import { Box, Flex, styled } from "styled-system/jsx";
import {
  LogOut,
  HelpCircle,
  NavArrowDown,
  SystemRestart,
  OpenNewWindow,
  RefreshCircle,
  Search,
  BadgeCheck,
  Code,
} from "iconoir-react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useHelpScout,
  useLoggedInAs,
  useLoginRedirectPath,
  clearLoginRedirectDeepLinkParam,
} from "shared/hooks";
import {
  EnumOwnerType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  EnumUserSystemPermissions,
  EnumUserUserType,
  useApiAccessByOrgQuery,
  usePersonDetailsLazyQuery,
} from "gql/generated.ts";
import {
  GlobalNotes,
  NavigationBlocker,
  OperatorMenu,
  OrganizationMenu,
  OrgOverviewSearch,
  StorefrontMenu,
  WhereaboutsLogoMark,
} from "shared/components";
import { DeveloperZoneSlideout } from "shared/components/root/DeveloperZone";
import { useTranslation } from "react-i18next";
import { useCurrentAppRoute } from "shared/hooks";
import { useAppContext } from "useAppContext";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { css } from "styled-system/css";
import { useAppVersionContext } from "useAppVersionContext";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { useWorkbenchSession } from "shared/contextProviders/workbenchSession";
import { WorkbenchChatSlideout } from "shared/components/organisms/Workbench";
import { ImpersonationModal } from "./ImpersonationModal";
import { PersonContactInfoModal } from "shared/components/organisms/PersonDetailsModal/modals/PersonContactInfoModal/PersonContactInfoModal";
import { getCloudinary } from "shared/cloudinary";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { Link, useNavigate, useSearchParams } from "react-router";

enum MenuActions {
  Logout = "logout",
  SupportDocumentation = "supportDocumentation",
}

/** Home “Edit profile” pill: after avatar grow, badge fades in → label → hold → icon-only */
const EDIT_PROFILE_BADGE_FADE_MS = 160;
const EDIT_PROFILE_TEXT_HOLD_MS = 3000;
/** If off-DOM / font timing yields 0px measure, still open the track */
const EDIT_PROFILE_LABEL_WIDTH_FALLBACK_PX = 200;
/** Reveal: longer + strong ease-out so the end eases in gently (tweak duration / bezier to taste) */
const EDIT_PROFILE_LABEL_TRACK_EXPAND_TRANSITION =
  "width 0.72s cubic-bezier(0.16, 1, 0.3, 1)";
/** Collapse: slightly quicker, symmetric ease — keeps close from feeling sluggish */
const EDIT_PROFILE_LABEL_TRACK_COLLAPSE_TRANSITION =
  "width 0.46s cubic-bezier(0.45, 0, 0.55, 1)";

type RootHeaderProps = {
  showGlobalRouteSearchTrigger?: boolean;
  onOpenGlobalRouteSearch?: () => void;
};

export const RootHeader = React.memo(function RootHeader({
  showGlobalRouteSearchTrigger = false,
  onOpenGlobalRouteSearch,
}: RootHeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth0();
  const loggedInAs = useLoggedInAs();
  const appRoute = useCurrentAppRoute();
  const { clearLoginRedirectPath } = useLoginRedirectPath();
  const { currentVersion } = useAppVersionContext();

  const {
    dbUser,
    dbPerson,
    dbUserLoading,
    dbPersonLoading,
    refetchDbPerson,
    refetchDbUser,
  } = useAppContext();

  const profilePhotoUrl = useMemo(() => {
    const photoId = dbPerson?.photo?._id ?? dbUser?.photo?._id;
    if (!photoId) return null;
    const dpr = window.devicePixelRatio ?? 1;
    const size = Math.round(36 * dpr);
    return getCloudinary(photoId)
      .image(photoId)
      .addTransformation("f_auto")
      .resize(limitFill().width(size).height(size))
      .toURL();
  }, [dbPerson?.photo?._id, dbUser?.photo?._id]);

  /** Wait for Mongo user + person before falling back to Auth0 `user.picture` (avoids default green “initials” avatar flash). */
  const waitingForAvatarMetadata =
    dbUserLoading || (!!dbUser?._id && dbPersonLoading);

  const avatarPhotoUrl =
    profilePhotoUrl ??
    (!waitingForAvatarMetadata ? user?.picture ?? null : null);
  const avatarSize = profilePhotoUrl ? "36px" : "24px";

  const [avatarImageReady, setAvatarImageReady] = useState(false);
  const [avatarImageFailed, setAvatarImageFailed] = useState(false);
  /** After decode: start at small scale, then set true after rAF to run CSS transition to full size */
  const [avatarGrowExpanded, setAvatarGrowExpanded] = useState(false);

  useEffect(() => {
    if (!avatarPhotoUrl) {
      setAvatarImageFailed(false);
      setAvatarImageReady(true);
      setAvatarGrowExpanded(false);
      return;
    }
    setAvatarImageFailed(false);
    setAvatarImageReady(false);
    setAvatarGrowExpanded(false);
  }, [avatarPhotoUrl]);

  useLayoutEffect(() => {
    if (!avatarImageReady || !avatarPhotoUrl || avatarImageFailed) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setAvatarGrowExpanded(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [avatarImageReady, avatarPhotoUrl, avatarImageFailed]);

  const isAdmin = dbUser?.userType === EnumUserUserType.TnbInternal;
  const isAdminlandRoute = appRoute.root === "/app/admin";
  const isDeveloper = useMemo(
    () =>
      !!dbUser?.access?.organizations?.some(
        o =>
          o?.role === EnumUserAccessOperatorsRole.Developer &&
          o?.status === EnumUserAccessOperatorsStatus.Accepted,
      ),
    [dbUser?.access?.organizations],
  );
  const { data: developerZoneApiAccessData } = useApiAccessByOrgQuery({
    skip: !dbUser,
    fetchPolicy: "cache-first",
  });
  const hasDeveloperZoneApiKeys =
    (developerZoneApiAccessData?.apiAccessByOrg?.length ?? 0) > 0;
  const showDeveloperZone = isDeveloper || hasDeveloperZoneApiKeys;
  const [isDeveloperZoneOpen, setIsDeveloperZoneOpen] = useState(false);
  const { isImpersonating } = useImpersonation();
  const { isTesting: isWorkbenchTesting } = useWorkbenchSession();
  const showImpersonation =
    !isImpersonating &&
    isAdmin &&
    dbUser?.systemPermissions?.includes(
      EnumUserSystemPermissions.Impersonation,
    );
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] =
    useState(false);

  const isHomePage = !loggedInAs;

  const [isContactInfoOpen, setIsContactInfoOpen] = useState(false);
  const [profileSection, setProfileSection] = useState<
    "notifications" | undefined
  >();
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [personDetails, setPersonDetails] = useState<any>(null);
  const [fetchPersonDetails] = usePersonDetailsLazyQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromEditProfileLink = useRef(false);

  const entityContext = useMemo(() => {
    const firstOrg = dbUser?.access?.organizations?.[0];
    if (firstOrg?.entityId)
      return { entityId: firstOrg.entityId, type: EnumOwnerType.Organization };
    const firstSf = dbUser?.access?.storefronts?.[0];
    if (firstSf?.entityId)
      return { entityId: firstSf.entityId, type: EnumOwnerType.Storefront };
    const firstOp = dbUser?.access?.operators?.[0];
    if (firstOp?.entityId)
      return { entityId: firstOp.entityId, type: EnumOwnerType.Operator };
    return null;
  }, [dbUser?.access]);

  /** Staged intro only when a real photo animates (grow → badge sequence). */
  const useStagedEditProfileBadge =
    isHomePage &&
    !!entityContext &&
    !!avatarPhotoUrl &&
    !avatarImageFailed;

  type EditProfileIntroStep =
    | "waiting"
    | "badge_fade"
    | "with_text"
    | "compact";

  const [editProfileIntroStep, setEditProfileIntroStep] =
    useState<EditProfileIntroStep>("waiting");
  const [badgeIntroOpacity, setBadgeIntroOpacity] = useState(1);

  useEffect(() => {
    if (!useStagedEditProfileBadge || !avatarGrowExpanded) {
      setEditProfileIntroStep("waiting");
      return;
    }
    setEditProfileIntroStep("badge_fade");
    const tRevealLabel = window.setTimeout(() => {
      setEditProfileIntroStep("with_text");
    }, EDIT_PROFILE_BADGE_FADE_MS);
    const tCollapse = window.setTimeout(() => {
      setEditProfileIntroStep("compact");
    }, EDIT_PROFILE_BADGE_FADE_MS + EDIT_PROFILE_TEXT_HOLD_MS);
    return () => {
      window.clearTimeout(tRevealLabel);
      window.clearTimeout(tCollapse);
    };
  }, [useStagedEditProfileBadge, avatarGrowExpanded]);

  useEffect(() => {
    if (editProfileIntroStep !== "badge_fade") {
      setBadgeIntroOpacity(1);
      return;
    }
    setBadgeIntroOpacity(0);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setBadgeIntroOpacity(1));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [editProfileIntroStep]);

  const showEditProfileLabel = useStagedEditProfileBadge
    ? editProfileIntroStep === "with_text"
    : true;

  const editProfileLabelMeasureRef = useRef<HTMLDivElement | null>(null);
  const [editProfileLabelWidthPx, setEditProfileLabelWidthPx] = useState(0);

  useLayoutEffect(() => {
    if (!useStagedEditProfileBadge) {
      setEditProfileLabelWidthPx(0);
      return;
    }
    const el = editProfileLabelMeasureRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.max(
        rect.width,
        el.scrollWidth,
        el.offsetWidth,
      );
      if (w > 0) setEditProfileLabelWidthPx(w);
    };
    measure();
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [useStagedEditProfileBadge, i18n.language, showEditProfileLabel]);

  const editProfileLabelTrackWidthPx = useMemo(() => {
    if (!showEditProfileLabel) return 0;
    return editProfileLabelWidthPx > 0
      ? editProfileLabelWidthPx
      : EDIT_PROFILE_LABEL_WIDTH_FALLBACK_PX;
  }, [showEditProfileLabel, editProfileLabelWidthPx]);

  const editProfileLabelTrackTransition = useMemo(
    () =>
      !useStagedEditProfileBadge
        ? undefined
        : showEditProfileLabel
          ? EDIT_PROFILE_LABEL_TRACK_EXPAND_TRANSITION
          : EDIT_PROFILE_LABEL_TRACK_COLLAPSE_TRANSITION,
    [useStagedEditProfileBadge, showEditProfileLabel],
  );

  const handleEditProfile = useCallback(async () => {
    if (!dbPerson?._id || !entityContext) return;
    setIsLoadingProfile(true);
    try {
      const { data } = await fetchPersonDetails({
        variables: {
          personId: dbPerson._id,
          entityId: entityContext.entityId,
          type: entityContext.type,
        },
      });
      if (data?.personDetails) {
        setPersonDetails(data.personDetails);
        setIsContactInfoOpen(true);
      }
    } catch {
      // silently fail — the button is a convenience affordance
    } finally {
      setIsLoadingProfile(false);
    }
  }, [dbPerson?._id, entityContext, fetchPersonDetails]);

  useEffect(() => {
    if (searchParams.get("editProfile") !== "notifications") return;
    if (!dbPerson?._id || !entityContext || openedFromEditProfileLink.current) {
      return;
    }
    openedFromEditProfileLink.current = true;
    setProfileSection("notifications");
    void handleEditProfile();
    const next = new URLSearchParams(searchParams);
    next.delete("editProfile");
    setSearchParams(next, { replace: true });
    clearLoginRedirectDeepLinkParam("editProfile");
  }, [searchParams, dbPerson?._id, entityContext, handleEditProfile, setSearchParams]);

  const handleCloseContactInfo = useCallback(() => {
    setIsContactInfoOpen(false);
    void Promise.all([refetchDbPerson(), refetchDbUser()]);
    setTimeout(() => setPersonDetails(null), 400);
  }, [refetchDbPerson, refetchDbUser]);

  const navigate = useNavigate();
  const { handleBeaconClick } = useHelpScout();

  const handleAdminlandClick = () => {
    if (!isAdminlandRoute) {
      sessionStorage.setItem(
        "waPreAdminlandPath",
        window.location.pathname + window.location.search,
      );
      navigate("/app/admin");
    } else {
      const savedPath = sessionStorage.getItem("waPreAdminlandPath");
      sessionStorage.removeItem("waPreAdminlandPath");
      navigate(savedPath || "/app");
    }
  };

  const handleLogout = () => {
    clearLoginRedirectPath();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  const handleMenuSelection = (value: MenuActions) => {
    switch (value) {
      case MenuActions.Logout:
        handleLogout();
        break;
      case MenuActions.SupportDocumentation:
        window.open("https://whereabouts.helpscoutdocs.com", "_blank");
        break;
      default:
        break;
    }
  };

  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"center"}
      gap="100"
      backgroundColor={"white"}
      px={{
        base: "300",
        md: "400",
      }}
      py={{
        base: "200",
        md: "250",
      }}
      borderBottomWidth={1}
      borderBottomColor={"border.light"}
    >
      <Flex alignItems={"center"} gap={"150"}>
        <NavigationBlocker>
          <ReactRouterBaseLink
            to={"/app"}
            borderRadius={"full"}
            width={avatarSize}
            height={avatarSize}
            minWidth={avatarSize}
            minHeight={avatarSize}
            maxWidth={avatarSize}
            maxHeight={avatarSize}
            aspectRatio="1 / 1"
            flexShrink={0}
            display={"flex"}
            overflow="hidden"
            _focusVisible={{
              outlineColor: "border.light",
              outlineOffset: "2px",
              outlineWidth: "1px",
              outlineStyle: "solid",
            }}
          >
            {waitingForAvatarMetadata ? (
              <Flex
                w="full"
                h="full"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner w="16px" h="16px" />
              </Flex>
            ) : avatarPhotoUrl && !avatarImageFailed ? (
              <Box
                position="relative"
                w="full"
                h="full"
                minW={avatarSize}
                minH={avatarSize}
                maxW={avatarSize}
                maxH={avatarSize}
                aspectRatio="1 / 1"
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                borderRadius="full"
              >
                {!avatarImageReady && (
                  <Flex
                    position="absolute"
                    inset={0}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Spinner w="16px" h="16px" />
                  </Flex>
                )}
                <styled.img
                  src={avatarPhotoUrl}
                  borderRadius={"full"}
                  alt={user?.name}
                  display="block"
                  w="full"
                  h="full"
                  minW="full"
                  minH="full"
                  maxW="full"
                  maxH="full"
                  aspectRatio="1 / 1"
                  objectFit="cover"
                  opacity={avatarImageReady ? 1 : 0}
                  transform={
                    avatarImageReady
                      ? avatarGrowExpanded
                        ? "scale(1)"
                        : "scale(0.42)"
                      : "scale(0.42)"
                  }
                  transformOrigin="center"
                  transition={
                    avatarGrowExpanded
                      ? "transform 0.38s cubic-bezier(0.34, 1.48, 0.64, 1)"
                      : "none"
                  }
                  onLoad={() => setAvatarImageReady(true)}
                  onError={() => setAvatarImageFailed(true)}
                />
              </Box>
            ) : (
              <Text color={"ink.50"}>
                {user?.name?.substring(0, 1).toUpperCase()}
              </Text>
            )}
          </ReactRouterBaseLink>
        </NavigationBlocker>
        {isHomePage && entityContext &&
          (useStagedEditProfileBadge
            ? editProfileIntroStep !== "waiting"
            : true) && (
            <styled.button
              type="button"
              onClick={() => {
                setProfileSection(undefined);
                void handleEditProfile();
              }}
              position="relative"
              display="inline-flex"
              alignItems="center"
              justifyContent="flex-start"
              gap="0"
              px="200"
              py="50"
              borderRadius="full"
              bg="bg.accent.blue.dark"
              cursor="pointer"
              border="none"
              opacity={
                useStagedEditProfileBadge &&
                editProfileIntroStep === "badge_fade"
                  ? badgeIntroOpacity
                  : 1
              }
              transition={
                useStagedEditProfileBadge &&
                editProfileIntroStep === "badge_fade"
                  ? "opacity 0.16s ease-out"
                  : "opacity 0.15s ease"
              }
              _hover={{ opacity: 0.85 }}
              title={t("core:HOME_PAGE.EDIT_PROFILE")}
            >
              {isLoadingProfile ? (
                <Box
                  w="16px"
                  h="16px"
                  animation="spin"
                  borderRadius="full"
                  border="2px solid"
                  borderColor="white"
                  borderTopColor="transparent"
                />
              ) : (
                <Icon Element={BadgeCheck} color="white" size="sm" />
              )}
              {useStagedEditProfileBadge ? (
                <>
                  {/*
                    In-flow measurement: `position:fixed; left:-9999px` often reports 0×0.
                    Absolutely positioned inside the relative button + opacity 0 keeps a real layout size.
                  */}
                  <div
                    ref={editProfileLabelMeasureRef}
                    className={css({
                      position: "absolute",
                      left: "0",
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: "0",
                      pl: "100",
                      display: "inline-flex",
                      alignItems: "center",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      zIndex: 0,
                    })}
                    aria-hidden
                  >
                    <Text
                      visual="smallSemiBold"
                      color="white"
                      whiteSpace="nowrap"
                    >
                      {t("core:HOME_PAGE.EDIT_PROFILE")}
                    </Text>
                  </div>
                  <div
                    aria-hidden={!showEditProfileLabel}
                    className={css({
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      flexShrink: "0",
                      position: "relative",
                      zIndex: "1",
                    })}
                    style={{
                      width: editProfileLabelTrackWidthPx,
                      transition: editProfileLabelTrackTransition,
                    }}
                  >
                    <Box pl="100" display="inline-flex" alignItems="center">
                      <Text
                        visual="smallSemiBold"
                        color="white"
                        whiteSpace="nowrap"
                      >
                        {t("core:HOME_PAGE.EDIT_PROFILE")}
                      </Text>
                    </Box>
                  </div>
                </>
              ) : (
                <Box pl="100" display="inline-flex" alignItems="center">
                  <Text
                    visual="smallSemiBold"
                    color="white"
                    whiteSpace="nowrap"
                  >
                    {t("core:HOME_PAGE.EDIT_PROFILE")}
                  </Text>
                </Box>
              )}
            </styled.button>
          )}
        {loggedInAs?.type === EnumOwnerType.Operator && (
          <OperatorMenu operatorEntityId={loggedInAs.entityId} />
        )}
        {loggedInAs?.type === EnumOwnerType.Organization && (
          <OrganizationMenu organizationId={loggedInAs.entityId} />
        )}
        {loggedInAs?.type === EnumOwnerType.Storefront && (
          <StorefrontMenu storefrontId={loggedInAs.entityId} />
        )}
      </Flex>

      <Flex alignItems={"center"} justifyContent={"flex-end"} gap={"200"}>
        <Flex alignItems={"center"} justifyContent={"flex-end"} gap={"150"}>
          <Button
            visual="unstyled"
            Icon={HelpCircle}
            onClick={() => handleBeaconClick({})}
            p="0"
            borderRadius={"full"}
            transitionProperty={"transform"}
            transitionDuration={"0.2s"}
            _hover={{
              transform: "scale(1.1)",
            }}
            _focusVisible={{
              transform: "scale(1.1)",
            }}
            title={t("core:SUPPORT")}
          />
          {loggedInAs?.type === EnumOwnerType.Organization && (
            <OrgOverviewSearch organizationId={loggedInAs.entityId} />
          )}
          {loggedInAs?.type === EnumOwnerType.Organization && <GlobalNotes />}
          {showGlobalRouteSearchTrigger && (
            <Button
              visual="unstyled"
              Icon={Search}
              onClick={onOpenGlobalRouteSearch}
              p="0"
              borderRadius={"full"}
              transitionProperty={"transform"}
              transitionDuration={"0.2s"}
              _hover={{
                transform: "scale(1.1)",
              }}
              _focusVisible={{
                transform: "scale(1.1)",
              }}
              title="Open global route search (Option+W / Alt+W)"
            />
          )}
          {showDeveloperZone && (
            <Button
              visual="unstyled"
              Icon={Code}
              onClick={() => setIsDeveloperZoneOpen(true)}
              p="0"
              borderRadius={"full"}
              transitionProperty={"transform"}
              transitionDuration={"0.2s"}
              _hover={{
                transform: "scale(1.1)",
              }}
              _focusVisible={{
                transform: "scale(1.1)",
              }}
              title={t("core:DEVELOPER_ZONE.TITLE")}
            />
          )}
          {isAdmin && (
            <NavigationBlocker>
              <styled.button
                type="button"
                cursor={"pointer"}
                onClick={handleAdminlandClick}
                color={isAdminlandRoute ? "white" : "icon.warning.mid"}
                bg={isAdminlandRoute ? "bg.warning.dark" : "transparent"}
                border={"none"}
                borderRadius={"full"}
                height={"24px"}
                width={"24px"}
                transitionProperty={"transform, color, background"}
                transitionDuration={"0.2s"}
                _hover={{
                  transform: "scale(1.1)",
                }}
                _focusVisible={{
                  transform: "scale(1.1)",
                  outlineColor: "border.light",
                  outlineOffset: "2px",
                  outlineWidth: "1px",
                  outlineStyle: "solid",
                }}
                title={t("core:ADMINLAND")}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                p={0}
              >
                <Icon
                  Element={SystemRestart}
                  size={isAdminlandRoute ? "sm" : "md"}
                />
              </styled.button>
            </NavigationBlocker>
          )}
        </Flex>
        <Menu.Root
          trigger={
            <styled.button
              type="button"
              borderRadius={"100"}
              bg={"bg.subtlest"}
              p={"100"}
              border={"1px solid"}
              borderColor={"border.light"}
              gap={"150"}
              _hover={{
                bg: "bg.subtle",
              }}
              _focusVisible={{
                bg: "bg.subtle",
                outline: "none",
              }}
              transitionProperty={"background"}
              transitionDuration={"normal"}
              transitionTimingFunction={"default"}
              display={"flex"}
              alignItems={"center"}
              cursor={"pointer"}
            >
              <WhereaboutsLogoMark width={"20px"} height={"20px"} />
              <Icon Element={NavArrowDown} size={"sm"} color={"icon.light"} />
            </styled.button>
          }
          positioning={{
            placement: "bottom-end",
            offset: {
              crossAxis: -28,
            },
          }}
          onSelect={({ value }: { value: string }) =>
            handleMenuSelection(value as MenuActions)
          }
          _contentProps={{
            className: css({ p: "1px" }),
          }}
        >
          <NavigationBlocker>
            <StyledMenuItem value={MenuActions.SupportDocumentation}>
              <Text visual="smallMedium" color="text.mid">
                {t("core:HELP_CENTER")}
              </Text>
              <Icon Element={OpenNewWindow} size="sm" color="icon.light!" />
            </StyledMenuItem>
            <StyledMenuItem value={MenuActions.Logout}>
              <Text visual={"smallMedium"} color={"text.mid"}>
                {t("core:LOGOUT")}
              </Text>
              <Icon Element={LogOut} size={"sm"} color={"icon.light!"} />
            </StyledMenuItem>
            <Flex
              flexDir="column"
              gap="75"
              py="100"
              px="150"
              bg="bg.subtlest"
              borderBottomRadius={"100"}
            >
              <Flex alignItems="center" justifyContent="space-between" gap="75">
                <Text
                  fontSize={"3xs"}
                  lineHeight={"2xs"}
                  color={"text.light"}
                  lineClamp={1}
                  wordBreak="break-all"
                  flex={1}
                  minW={0}
                >
                  {dbUser?.email}
                </Text>
                {showImpersonation && (
                  <styled.button
                    type="button"
                    bg="transparent"
                    border="none"
                    outline="none"
                    cursor="pointer"
                    p="0"
                    display="flex"
                    alignItems="center"
                    flexShrink={0}
                    color="icon.light"
                    _hover={{ color: "icon.danger.mid" }}
                    _focusVisible={{ color: "icon.danger.mid" }}
                    transition="color 0.15s"
                    title="Impersonate user"
                    onClick={() => setIsImpersonationModalOpen(true)}
                  >
                    <Icon Element={RefreshCircle} size="sm" />
                  </styled.button>
                )}
              </Flex>
              <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="150"
              >
                <Text fontSize={"3xs"} lineHeight={"2xs"} color={"text.light"}>
                  Whereabouts
                </Text>
                <Text fontSize={"3xs"} lineHeight={"2xs"} color={"text.light"}>
                  v{currentVersion}
                </Text>
              </Flex>
            </Flex>
          </NavigationBlocker>
        </Menu.Root>
        <ImpersonationModal
          isOpen={isImpersonationModalOpen}
          onClose={() => setIsImpersonationModalOpen(false)}
        />
        {isWorkbenchTesting && <WorkbenchChatSlideout />}
        {showDeveloperZone && (
          <DeveloperZoneSlideout
            isOpen={isDeveloperZoneOpen}
            onClose={() => setIsDeveloperZoneOpen(false)}
          />
        )}
      </Flex>
      {personDetails && entityContext && (
        <PersonContactInfoModal
          isOpen={isContactInfoOpen}
          onClose={handleCloseContactInfo}
          person={personDetails}
          entityContext={entityContext}
          placement="leftPane"
          variant="editProfile"
          initialSection={profileSection}
        />
      )}
    </Flex>
  );
});

const StyledMenuItem = styled(Menu.Item, {
  base: {
    minW: "180px",
    borderBottom: "1px solid",
    borderBottomColor: "border.lightest",
    borderBottomRadius: "0",
  },
});

const ReactRouterBaseLink = styled(Link);
